'use client';

import type { CaseStudy } from '../../lib/types';
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import CursorTooltip from '../ui/CursorTooltip';

interface CaseStudyGridProps {
  caseStudies: CaseStudy[];
}

export default function CaseStudyGrid({ caseStudies }: CaseStudyGridProps) {
  const [filter, setFilter] = useState('all');
  
  const categories = ['all', ...Array.from(new Set(caseStudies.map(cs => cs.category)))];
  const filteredCaseStudies = filter === 'all' 
    ? caseStudies 
    : caseStudies.filter(cs => cs.category === filter);

  return (
    <div>
      {/* Filter Tabs */}
      <div className="flex justify-center mb-12">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-none">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 font-medium capitalize transition-colors ${
                filter === category
                  ? 'bg-black text-white'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Case Studies Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredCaseStudies.map((caseStudy) => (
          <CursorTooltip
            key={caseStudy.slug}
            tooltipText="Dive Deeper"
            backgroundColor="#AAFA96"
            textColor="#000000"
          >
            <Link 
              href={`/case-studies/${caseStudy.slug}`}
              className="group block bg-[#FDFEF6] overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
            >
            <div className="aspect-video bg-gray-200 overflow-hidden relative">
              <Image 
                src={caseStudy.featuredImage} 
                alt={caseStudy.title}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 uppercase tracking-wide">
                  {caseStudy.category}
                </span>
                <span className="text-sm text-gray-500">
                  {caseStudy.year}
                </span>
              </div>
              <h3 className="text-xl font-semibold mb-2 group-hover:text-blue-600 transition-colors">
                {caseStudy.title}
              </h3>
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                {caseStudy.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  {caseStudy.client}
                </span>
                <span className="text-sm text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                  View →
                </span>
              </div>
            </div>
          </Link>
          </CursorTooltip>
        ))}
      </div>

      {filteredCaseStudies.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No case studies found for this category.</p>
        </div>
      )}
    </div>
  );
}
